import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'baseline-collection' | 'structural-work-foundation' | 'transition-to-diagnosis' | 'diagnostic-questions' | 'full-work-diagnostic';
export function DeepWorkDecode() {
  const navigate = useNavigate();
  const [isAuthenticated] = useState(true);
  const [hasBaseline] = useState(false);
  const [hasQuickWorkDecode] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasBaseline ? 'structural-work-foundation' : 'baseline-collection');
  const [showExplanation, setShowExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  const [questionProgress, setQuestionProgress] = useState(0);
  // Baseline data (reusing QuickSelfDecode structure)
  const [baselineData, setBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Diagnostic questions data
  const [diagnosticData, setDiagnosticData] = useState({
    earlyWorkConditioning: [] as string[],
    roleRepetition: [] as string[],
    authorityTension: '',
    pressureConversion: '',
    burnoutMechanics: [] as string[],
    awarenessTiming: '',
    workHistoryContext: ''
  });
  // Structural work foundation (baseline translated to work)
  const structuralWorkFoundation = {
    structuralRole: 'You operate as a system stabilizer inside work environments. You naturally move toward building structure, organizing complexity, and creating clarity before executing. This is not learned behavior — it is how your system engages with work. You prefer ownership over execution, depth over speed, and behind-the-scenes architecture over high-visibility performance.',
    authorityRelationship: 'Your structural relationship to authority is one of alignment when clarity exists, and friction when directives feel misaligned or poorly defined. You do not resist authority directly, but you over-function when you perceive gaps in leadership or system design. This can create tension when authority expects execution without questioning.',
    pressureOutputPattern: 'Moderate pressure energizes this system — it provides focus and urgency. High or sustained pressure, however, triggers internal analysis and can delay action. When pressure feels misaligned with your understanding of what matters, output drops sharply. The system does not break under pressure — it withdraws into recalibration.'
  };
  // Full diagnostic output
  const fullDiagnosticOutput = {
    workPatternMechanics: 'When work pressure rises or authority feels unclear, the system automatically moves into analysis and structure-building mode. The trigger is ambiguity or misalignment; the response is to map, organize, and stabilize. This creates short-term clarity but can delay execution and create friction with authority that expects immediate action. The pattern reinforces itself: clarity feels like progress, which rewards the pattern, making it more automatic.',
    patternOrigin: 'This work pattern likely formed in early work environments where clarity was scarce, authority was inconsistent, or you were rewarded for stabilizing chaos. Early experiences may have taught you that understanding the system = safety, and that pattern became structural. The first authority figures who punished ambiguity or rewarded precision locked this in.',
    constraintMap: 'The pattern stays active at work because: (1) it reliably reduces anxiety in ambiguous situations, (2) many work environments reward analysis over embodied action, (3) the cost (delayed execution, authority friction) is less visible than the benefit (internal clarity). The constraint is not external pressure — it is that the pattern works well enough to stay invisible.',
    crossRoleRepetition: 'This pattern repeats across roles and companies. In every new role, you initially stabilize and build structure. Then, as pressure rises or authority becomes unclear, you over-function to compensate. Eventually, the system exhausts from carrying more responsibility than authority. You leave or disengage, then the pattern restarts in the next role. The roles change; the pattern does not.',
    burnoutGrowthBoundary: 'This pattern caps growth when roles require high-visibility execution over behind-the-scenes architecture, or when authority expects speed over depth. Burnout accumulates when you carry responsibility without corresponding authority, or when sustained ambiguity prevents the system from stabilizing. The pattern does not break suddenly — it erodes slowly through over-functioning until disengagement is the only option.',
    whatChangedFromSnapshot: {
      quickWorkSnapshot: 'Quick Work Decode showed your structural work orientation (system stabilizer, autonomy preference, depth over speed), authority response (alignment when clear, friction when ambiguous), and blind spot (friction when speed trumps depth).',
      deepDiagnosis: 'The deep diagnosis revealed: how the pattern formed (early work conditioning around clarity and authority), how it reinforces itself (clarity = reward = more automatic), what keeps it active (short-term anxiety reduction, invisible costs), where it repeats across roles (stabilize → over-function → exhaust → leave), and where it caps growth or leads to burnout (responsibility without authority, sustained ambiguity).',
      whySnapshotInsufficient: 'The snapshot shows how you structurally operate at work. Deep diagnosis shows how the pattern formed, why it stays active, where it repeats across your career, and where it creates friction or caps growth. Snapshot is structure; diagnosis is mechanism, origin, and long-term repetition.'
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleBaselineSubmit = () => {
    if (!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country) return;
    if ((baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating structural baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('structural-work-foundation');
    }, 2000);
  };
  const handleStartDiagnosis = () => {
    setCurrentPhase('transition-to-diagnosis');
  };
  const handleBeginQuestions = () => {
    setCurrentPhase('diagnostic-questions');
    setQuestionProgress(0);
  };
  const handleDiagnosticSubmit = () => {
    if (!diagnosticData.authorityTension || !diagnosticData.pressureConversion || !diagnosticData.awarenessTiming) return;
    if (diagnosticData.earlyWorkConditioning.length === 0 || diagnosticData.roleRepetition.length === 0) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating full work diagnostic...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('full-work-diagnostic');
    }, 3000);
  };
  const handleToggleMultiSelect = (field: keyof typeof diagnosticData, value: string) => {
    setDiagnosticData(prev => {
      const currentArray = prev[field] as string[];
      return {
        ...prev,
        [field]: currentArray.includes(value) ? currentArray.filter(v => v !== value) : [...currentArray, value]
      };
    });
  };
  if (isGenerating) {
    return <>
        <Helmet data-id="element-1181">
          <title data-id="element-1182">Generating Work Diagnostic | Lazy Monk Labs</title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-1183">
          <div className="text-center" data-id="element-1184">
            <div className="mb-6" data-id="element-1185">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-1186"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-1187">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-1188">
        <title data-id="element-1189">Deep Work Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-1190">
        <header data-id="element-1191">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-1192">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1193">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-1194">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-1195" />
                <span className="text-lg font-medium tracking-tight" data-id="element-1196">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-1197">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-1198">
          <div className="max-w-2xl mx-auto" data-id="element-1199">
            {/* PHASE 1: Baseline Collection - Same as other flows, will continue in next message */}

            {/* PHASE 1: Baseline Collection (Same as DeepSelfDecode) */}
            {currentPhase === 'baseline-collection' && <div data-id="element-1200">
                <div className="mb-12" data-id="element-1201">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1202">
                    Deep Work Decode
                  </h1>
                  <p className="text-lg text-[#F5F5F5]/60 italic mb-4" data-id="element-1203">
                    A full diagnostic of how your internal patterns interact
                    with work systems
                  </p>
                  <p className="text-base text-[#F5F5F5]/70 leading-relaxed" data-id="element-1204">
                    This report maps why work triggers certain patterns, how
                    they repeat across roles, and what keeps them active over
                    time.
                  </p>
                </div>

                {/* Baseline collection UI - same as other flows */}
                <div className="space-y-8 mb-12" data-id="element-1205">
                  <div data-id="element-1206">
                    <label className="block text-sm font-medium mb-3" data-id="element-1207">
                      Date of birth
                    </label>
                    {baselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-1208">
                        <div className="text-2xl font-medium" data-id="element-1209">
                          {months.find(m => m.value === baselineData.month)?.label}{' '}
                          {baselineData.day}, {baselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-1210">
                      <div data-id="element-1211">
                        <label htmlFor="month" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-1212">
                          Month
                        </label>
                        <select id="month" value={baselineData.month} onChange={e => updateDateOfBirth(baselineData.year, e.target.value, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1213">
                          <option value="" data-id="element-1214">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-1215">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-1216">
                        <label htmlFor="day" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-1217">
                          Day
                        </label>
                        <select id="day" value={baselineData.day} onChange={e => updateDateOfBirth(baselineData.year, baselineData.month, e.target.value)} disabled={!baselineData.month || !baselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-1218">
                          <option value="" data-id="element-1219">Day</option>
                          {Array.from({
                        length: getDaysInMonth(baselineData.year, baselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-1220">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-1221">
                        <label htmlFor="year" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-1222">
                          Year
                        </label>
                        <select id="year" value={baselineData.year} onChange={e => updateDateOfBirth(e.target.value, baselineData.month, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1223">
                          <option value="" data-id="element-1224">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-1225">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-1226">
                    <label htmlFor="placeOfBirth" className="block text-sm font-medium mb-2" data-id="element-1227">
                      Place of birth
                    </label>
                    <input type="text" id="placeOfBirth" value={baselineData.placeOfBirth} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1228" />
                  </div>
                  <div data-id="element-1229">
                    <label htmlFor="country" className="block text-sm font-medium mb-2" data-id="element-1230">
                      Country
                    </label>
                    <input type="text" id="country" value={baselineData.country} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1231" />
                  </div>
                </div>
                <button onClick={handleBaselineSubmit} disabled={!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1232">
                  Generate Structural Baseline
                </button>
              </div>}

            {/* PHASE 2: Structural Work Foundation */}
            {currentPhase === 'structural-work-foundation' && <div data-id="element-1233">
                <div className="mb-12" data-id="element-1234">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1235">
                    Deep Work Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-1236">
                    Structural work foundation
                  </p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1237">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1238">
                    <span className="text-2xl" data-id="element-1239">🧭</span>
                    <div className="flex-1" data-id="element-1240">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1241">
                        Your structural role inside work systems
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1242">
                        {structuralWorkFoundation.structuralRole}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1243">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1244">
                    <span className="text-2xl" data-id="element-1245">🏗️</span>
                    <div className="flex-1" data-id="element-1246">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1247">
                        How this system relates to authority
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1248">
                        {structuralWorkFoundation.authorityRelationship}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1249">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1250">
                    <span className="text-2xl" data-id="element-1251">⚙️</span>
                    <div className="flex-1" data-id="element-1252">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1253">
                        How pressure converts into output or breakdown
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1254">
                        {structuralWorkFoundation.pressureOutputPattern}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 text-center" data-id="element-1255">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-1256">
                    Structural work baseline — context-independent
                  </p>
                </div>
                <div className="mb-12" data-id="element-1257">
                  <button onClick={() => setShowExplanation(!showExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-1258">
                    <span data-id="element-1259">📘</span>
                    <span data-id="element-1260">How this work diagnostic is generated</span>
                    <span className="text-xs" data-id="element-1261">
                      {showExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1262">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-1263">
                        This diagnostic starts with your structural baseline and
                        translates it into work systems — authority, ownership,
                        pressure, and output.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-1264">
                        Work does not create new patterns. It amplifies existing
                        ones.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1265">
                        The next questions diagnose how these patterns formed,
                        repeat, and lock in across your work life.
                      </p>
                    </div>}
                </div>
                <button onClick={handleStartDiagnosis} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1266">
                  Continue to Diagnosis
                </button>
              </div>}

            {/* PHASE 3: Transition */}
            {currentPhase === 'transition-to-diagnosis' && <div data-id="element-1267">
                <div className="mb-12" data-id="element-1268">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1269">
                    From work snapshot to work diagnosis
                  </h1>
                </div>
                <div className="mb-12" data-id="element-1270">
                  <p className="text-lg text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-1271">
                    The baseline shows how your structure behaves at work.
                  </p>
                  <p className="text-lg text-[#F5F5F5]/70 leading-relaxed" data-id="element-1272">
                    The next questions diagnose how this pattern developed,
                    repeats across roles, and stays active over time.
                  </p>
                </div>
                <button onClick={handleBeginQuestions} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1273">
                  Begin Work Diagnosis
                </button>
              </div>}

            {/* PHASE 4: Diagnostic Questions - Abbreviated for space, add full implementation */}
            {currentPhase === 'diagnostic-questions' && <div data-id="element-1274">
                <div className="mb-12" data-id="element-1275">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1276">
                    Work Diagnostic Questions
                  </h1>
                </div>
                <div className="space-y-12 mb-12" data-id="element-1277">
                  <div data-id="element-1278">
                    <h2 className="text-lg font-medium mb-3" data-id="element-1279">
                      What characterized your early work experiences?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-1280">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-1281">
                      {['Unclear authority or expectations', 'Rewarded for stabilizing chaos', 'Punished for ambiguity or mistakes', 'Required to over-function early', 'Inconsistent leadership'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.earlyWorkConditioning.includes(option) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1282">
                          <input type="checkbox" checked={diagnosticData.earlyWorkConditioning.includes(option)} onChange={() => handleToggleMultiSelect('earlyWorkConditioning', option)} className="sr-only" data-id="element-1283" />
                          <span className="text-base" data-id="element-1284">{option}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-1285">
                    <h2 className="text-lg font-medium mb-6" data-id="element-1286">
                      When work pressure rises, you usually…
                    </h2>
                    <div className="space-y-3" data-id="element-1287">
                      {['Analyze and plan extensively', 'Take more control', 'Pull back and observe', 'Push through with action'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.pressureConversion === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1288">
                          <input type="radio" name="pressureConversion" value={option} checked={diagnosticData.pressureConversion === option} onChange={e => setDiagnosticData(prev => ({
                      ...prev,
                      pressureConversion: e.target.value
                    }))} className="sr-only" data-id="element-1289" />
                          <span className="text-base" data-id="element-1290">{option}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-1291">
                    <h2 className="text-lg font-medium mb-3" data-id="element-1292">
                      Anything important about your work history or current
                      role?
                    </h2>
                    <textarea value={diagnosticData.workHistoryContext} onChange={e => setDiagnosticData(prev => ({
                  ...prev,
                  workHistoryContext: e.target.value
                }))} rows={4} maxLength={300} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional context..." data-id="element-1293" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-1294">
                      {diagnosticData.workHistoryContext.length}/300
                    </p>
                  </div>
                </div>
                <button onClick={handleDiagnosticSubmit} disabled={!diagnosticData.pressureConversion || diagnosticData.earlyWorkConditioning.length === 0} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1295">
                  Generate Full Work Diagnostic
                </button>
              </div>}

            {/* PHASE 5: Full Work Diagnostic - Abbreviated, add full sections */}
            {currentPhase === 'full-work-diagnostic' && <div data-id="element-1296">
                <div className="mb-12" data-id="element-1297">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1298">
                    Deep Work Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-1299">Full Diagnostic</p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1300">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1301">
                    <span className="text-2xl" data-id="element-1302">🔁</span>
                    <div className="flex-1" data-id="element-1303">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1304">
                        How your work pattern runs
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1305">
                        {fullDiagnosticOutput.workPatternMechanics}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1306">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1307">
                    <span className="text-2xl" data-id="element-1308">🧬</span>
                    <div className="flex-1" data-id="element-1309">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1310">
                        How this work pattern formed
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1311">
                        {fullDiagnosticOutput.patternOrigin}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1312">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1313">
                    <span className="text-2xl" data-id="element-1314">🔒</span>
                    <div className="flex-1" data-id="element-1315">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1316">
                        What keeps this pattern active at work
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1317">
                        {fullDiagnosticOutput.constraintMap}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1318">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1319">
                    <span className="text-2xl" data-id="element-1320">🌐</span>
                    <div className="flex-1" data-id="element-1321">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1322">
                        How this pattern repeats across roles
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1323">
                        {fullDiagnosticOutput.crossRoleRepetition}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1324">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1325">
                    <span className="text-2xl" data-id="element-1326">⚠️</span>
                    <div className="flex-1" data-id="element-1327">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1328">
                        Where this pattern caps growth or leads to burnout
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1329">
                        {fullDiagnosticOutput.burnoutGrowthBoundary}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12" data-id="element-1330">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-1331">
                    <span data-id="element-1332">🔍</span>
                    <span data-id="element-1333">
                      What this diagnosis revealed beyond the snapshot
                    </span>
                    <span className="text-xs" data-id="element-1334">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-1335">
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1336">
                        <h3 className="text-sm font-medium mb-4" data-id="element-1337">
                          What Quick Work Decode showed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1338">
                          {fullDiagnosticOutput.whatChangedFromSnapshot.quickWorkSnapshot}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1339">
                        <h3 className="text-sm font-medium mb-4" data-id="element-1340">
                          What deep diagnosis revealed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1341">
                          {fullDiagnosticOutput.whatChangedFromSnapshot.deepDiagnosis}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1342">
                        <h3 className="text-sm font-medium mb-4" data-id="element-1343">
                          Why snapshot alone was insufficient
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1344">
                          {fullDiagnosticOutput.whatChangedFromSnapshot.whySnapshotInsufficient}
                        </p>
                      </div>
                    </div>}
                </div>

                {/* CLOSURE PHASE 1: Pattern Consistency Check (Work-Specific) */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1345">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1346">
                    <span className="text-2xl" data-id="element-1347">🔁</span>
                    <div className="flex-1" data-id="element-1348">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1349">
                        Pattern Consistency Check
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-1350">
                        This pattern repeats across roles and organizations. The
                        companies change, the managers change, but the friction
                        points remain structurally similar.
                      </p>
                      <div className="space-y-4" data-id="element-1351">
                        <div data-id="element-1352">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1353">
                            Similar authority structures
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1354">
                            When authority is ambiguous or misaligned, the same
                            pattern activates. You over-function to create
                            structure, which creates short-term clarity but
                            long-term exhaustion. This happens regardless of the
                            specific manager or company culture.
                          </p>
                        </div>
                        <div data-id="element-1355">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1356">
                            Similar ambiguity conditions
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1357">
                            When role expectations are unclear or ownership is
                            undefined, the pattern triggers. You build internal
                            models to compensate for external ambiguity. The
                            pattern repeats in every role where clarity is
                            scarce, regardless of industry or seniority level.
                          </p>
                        </div>
                        <div data-id="element-1358">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1359">
                            Different companies, same friction
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1360">
                            Across different organizations, the same friction
                            emerges: you stabilize systems faster than they can
                            absorb, you carry more responsibility than
                            authority, and you disengage when the mismatch
                            becomes unsustainable. The pattern is not
                            company-specific — it is structural.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CLOSURE PHASE 2: Boundary Conditions (Work-Specific) */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1361">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1362">
                    <span className="text-2xl" data-id="element-1363">⚖️</span>
                    <div className="flex-1" data-id="element-1364">
                      <h2 className="text-xl font-medium mb-6" data-id="element-1365">
                        Where This Diagnosis Applies — and Where It Doesn't
                      </h2>
                      <div className="space-y-6" data-id="element-1366">
                        <div data-id="element-1367">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-1368">
                            This diagnosis explains:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-1369">
                            <li data-id="element-1370">
                              • Why work friction repeats across roles and
                              companies
                            </li>
                            <li data-id="element-1371">
                              • Why changing roles alone doesn't resolve the
                              pattern
                            </li>
                            <li data-id="element-1372">
                              • How authority structures amplify your internal
                              pattern
                            </li>
                            <li data-id="element-1373">
                              • What keeps the work pattern active despite
                              awareness
                            </li>
                          </ul>
                        </div>
                        <div data-id="element-1374">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-1375">
                            This diagnosis does NOT explain:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-1376">
                            <li data-id="element-1377">
                              • Job satisfaction in well-designed systems with
                              clear authority
                            </li>
                            <li data-id="element-1378">
                              • Skill gaps, technical performance, or capability
                              issues
                            </li>
                            <li data-id="element-1379">
                              • One-off workplace conflicts or interpersonal
                              dynamics
                            </li>
                            <li data-id="element-1380">
                              • Career trajectory, compensation, or external
                              success metrics
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CLOSURE PHASE 3: Integration Summary (Work-Specific) */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1381">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1382">
                    <span className="text-2xl" data-id="element-1383">🧭</span>
                    <div className="flex-1" data-id="element-1384">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1385">
                        Integration Summary
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1386">
                        This is a system that stabilizes work environments
                        through internal structure and control. Under pressure
                        or ambiguity, it over-functions to create clarity, which
                        creates short-term relief but long-term exhaustion. The
                        pattern repeats across roles because it reliably reduces
                        anxiety in unclear systems, even when it caps growth or
                        leads to burnout. The pattern is not the problem — the
                        mismatch between how you operate and how work systems
                        are structured is.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-1387">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1388">
                    💾 Save Work Diagnostic
                  </button>
                  <Link to="/report-detail?report=deep-self-decode" className="block w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors text-center" data-id="element-1389">
                    ➡️ Explore Deep Self Decode
                  </Link>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}